import { Box, Chip, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Fragment, useState } from 'react';
import { DownloadCloud, Plus } from 'react-feather';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FlagCountry from 'src/components/common/FlagCountry';
import IconFeather from 'src/components/common/Icon';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import CustomTable from 'src/components/Table';
import { StyledLinearProgressCreditUsage } from 'src/features/Activities/common';
import FormSearchGlobalTrade from 'src/features/AiReport/FormSearchGlobalTrade';
import { useQueryMyTradeDataReport } from 'src/services/queries/useQueryReport';
import { EmptyText, FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { ReportStatus } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';
import { TypeTradeDataReport } from 'src/utils/typings/response/report';

const TradeDataReport = () => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({
    limit: 25,
    offset: 0,
  });
  const { data, isFetching, refetch } = useQueryMyTradeDataReport(params);

  const columns: ConfigCell<TypeTradeDataReport>[] = [
    // {
    //   id: 'id',
    //   label: 'Id',
    //   width: 100,
    //   cell: (data: TypeTradeDataReport) => (
    //     <Tooltip title={data.id}>
    //       <Typography color={'text.secondary'} variant="body2">
    //         {truncate(data.id, { length: 6, omission: '' }) || EmptyText}
    //       </Typography>
    //     </Tooltip>
    //   ),
    // },
    // {
    //   id: 'file_name',
    //   label: 'File Name',
    //   cell: (data: TypeTradeDataReport) => (
    //     <Tooltip title={data.file_name}>
    //       <Typography color={'text.secondary'} variant="body2">
    //         {truncate(data.file_name, { length: 30 }) || EmptyText}
    //       </Typography>
    //     </Tooltip>
    //   ),
    // },
    {
      width: 170,
      id: 'file_url',
      label: 'File URL',
      cell: (data: TypeTradeDataReport) => {
        const isExpired = dayjs().isAfter(dayjs(data.expired_at));
        if (isExpired) return <Chip size="small" label="Expired" />;
        if (!data.file_url) return EmptyText;
        return (
          <Typography
            color={'secondary.main'}
            component={'a'}
            variant="body2"
            href={data.file_url}
            rel="noreferrer"
            download
          >
            <IconFeather muiProps={{ fontSize: 'small', sx: { marginRight: 1 } }} icon={<DownloadCloud />} />
            Download PDF
          </Typography>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      cell: (data: TypeTradeDataReport) => {
        const { status, progress } = data;
        const getColor = () => {
          switch (data.status) {
            case ReportStatus.SUCCESS:
              return 'success';
            case ReportStatus.FAILED:
              return 'error';
            case ReportStatus.PROCESSING:
              return 'secondary';
            default:
              return 'default';
          }
        };

        if (status === ReportStatus.PROCESSING) {
          return <StyledLinearProgressCreditUsage sx={{ height: 6 }} variant="determinate" value={progress} />;
        }

        return <Chip label={data.status} color={getColor()} size="small" />;
      },
    },
    {
      id: 'hs_code',
      label: 'HS Code',
      cell: (data: TypeTradeDataReport) => (
        <Typography color={'text.secondary'} variant="body2">
          {data.hs_code || EmptyText}
        </Typography>
      ),
    },
    {
      id: 'import_country_code',
      label: 'Import Country Code',
      width: 200,
      cell: (data: TypeTradeDataReport) => {
        const countryName = getCountryByCountryCode(data.import_country_code)?.label;
        return (
          <>
            {data.import_country_code ? (
              <Typography variant="body2" color={'text.secondary'}>
                <FlagCountry width={24} code={data.import_country_code} /> {countryName}
              </Typography>
            ) : (
              EmptyText
            )}
          </>
        );
      },
    },
    {
      id: 'is_only_global',
      label: 'Is Only Global',
      cell: (data: TypeTradeDataReport) => (
        <Typography color={'text.secondary'} variant="body2">
          {data.is_only_global ? 'Yes' : 'No'}
        </Typography>
      ),
    },
    {
      id: 'requested_at',
      label: 'Requested At',
      cell: (data: TypeTradeDataReport) => (
        <Typography color={'text.secondary'} variant="body2">
          {formatUTCTime(FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A, data.created_at, FORMAT_DATE_TIME.UTC_YEAR_FIRST)}
        </Typography>
      ),
    },
    {
      id: 'completed_at',
      label: 'Completed At',
      cell: (data: TypeTradeDataReport) => (
        <Typography color={'text.secondary'} variant="body2">
          {formatUTCTime(FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A, data.completed_at, FORMAT_DATE_TIME.UTC_YEAR_FIRST)}
        </Typography>
      ),
    },
  ];
  const handleOpenFormSearch = () => {
    setOpen(true);
  };
  return (
    <Fragment>
      <FormSearchGlobalTrade onSuccess={refetch} handleClose={() => setOpen(false)} open={open} />
      <WhiteBoxContent>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography variant="h5">Trade Data Full Report</Typography>
          <ButtonWithLoading
            onClick={handleOpenFormSearch}
            sx={{ alignSelf: 'right' }}
            startIcon={<Plus />}
            variant="contained"
          >
            Create new report
          </ButtonWithLoading>
        </Stack>
        <Box mt={3} />
        <CustomTable
          maxRow={50}
          totalRecords={data.total_records}
          onChangePage={(e) => setParams({ ...params, ...{ offset: e } })}
          onChangePageSize={(e) => setParams({ ...params, ...{ offset: 0, limit: e } })}
          page={params.offset}
          pageSize={params.limit}
          loading={isFetching}
          dataKey={'id'}
          data={getArrayDefault(data.data)}
          configColumn={columns}
        />
      </WhiteBoxContent>
    </Fragment>
  );
};

export default TradeDataReport;
